import { useState } from 'react'
import { useTranslation } from '@/contexts/TranslationContext'
import { Alert } from '@/components/ui'
import { formatPhone } from '@/lib/deviceDetection'

interface BillingProfileStepProps {
  initialData: {
    full_name: string
    whatsapp: string
  }
  onNext: (data: any) => void
  isLoading: boolean
}

export function BillingProfileStep({ initialData, onNext, isLoading }: BillingProfileStepProps) {
  const { t } = useTranslation()
  const [fullName, setFullName] = useState(initialData.full_name)
  const [whatsapp, setWhatsapp] = useState(initialData.whatsapp)
  const [errors, setErrors] = useState<Record<string, string>>({})

  const validate = () => {
    const newErrors: Record<string, string> = {}

    if (!fullName.trim()) {
      newErrors.fullName = t('onboarding.billing.profile.errors.nameRequired')
    } else if (fullName.trim().length < 3) {
      newErrors.fullName = t('onboarding.billing.profile.errors.nameMinLength')
    }

    if (whatsapp && whatsapp.replace(/\D/g, '').length < 10) {
      newErrors.whatsapp = t('onboarding.billing.profile.errors.whatsappInvalid')
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleNext = () => {
    if (!validate()) return

    onNext({
      full_name: fullName.trim(),
      whatsapp: whatsapp ? formatPhone(whatsapp) : ''
    })
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">
          {t('onboarding.billing.profile.title')}
        </h1>
        <p className="text-slate-600 dark:text-slate-400">
          {t('onboarding.billing.profile.subtitle')}
        </p>
      </div>

      {/* Form */}
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-lg p-8 space-y-6">
        {/* Full Name */}
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
            {t('onboarding.billing.profile.nameLabel')} <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            placeholder={t('onboarding.billing.profile.namePlaceholder')}
            disabled={isLoading}
            className={`w-full px-4 py-2 rounded-lg border ${
              errors.fullName ? 'border-red-300 dark:border-red-600' : 'border-slate-300 dark:border-slate-600'
            } bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 ${
              errors.fullName ? 'focus:ring-red-500' : 'focus:ring-emerald-500'
            } disabled:opacity-50 transition-colors`}
          />
          {errors.fullName && (
            <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.fullName}</p>
          )}
        </div>

        {/* WhatsApp */}
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
            {t('onboarding.billing.profile.whatsappLabel')}
          </label>
          <input
            type="tel"
            value={whatsapp}
            onChange={(e) => setWhatsapp(formatPhone(e.target.value))}
            placeholder={t('onboarding.billing.profile.whatsappPlaceholder')}
            disabled={isLoading}
            className={`w-full px-4 py-2 rounded-lg border ${
              errors.whatsapp ? 'border-red-300 dark:border-red-600' : 'border-slate-300 dark:border-slate-600'
            } bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 ${
              errors.whatsapp ? 'focus:ring-red-500' : 'focus:ring-emerald-500'
            } disabled:opacity-50 transition-colors`}
          />
          {errors.whatsapp && (
            <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.whatsapp}</p>
          )}
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            {t('onboarding.billing.profile.whatsappHelp')}
          </p>
        </div>
      </div>

      {/* Note */}
      <Alert variant="info">
        <p className="text-sm">{t('onboarding.billing.profile.note')}</p>
      </Alert>

      {/* Button */}
      <div className="flex justify-end">
        <button
          onClick={handleNext}
          disabled={isLoading}
          className="px-8 py-3 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-400 dark:disabled:bg-slate-700 text-white rounded-lg font-medium transition-colors"
        >
          {isLoading ? t('onboarding.billing.loading') : t('onboarding.billing.nextButton')}
        </button>
      </div>
    </div>
  )
}
